/**
 * Next.js Client Instrumentation
 *
 * This file is loaded early in the client-side lifecycle (Next.js 15.3+).
 * It is the SINGLE initialization point for PostHog analytics.
 *
 * Features initialized here:
 * - Exception capture (before React hydrates)
 * - Session recording
 * - Attribution tracking (UTM params, traffic source)
 * - Common event properties
 *
 * Helper functions for tracking events, page views, and user identification
 * are in src/lib/analytics/posthog.ts
 */

import posthog from "posthog-js";
import { isEmbeddedIntegrationPath } from "@/lib/monday-app/embedded-path";
import { installHistoryGuard } from "@/lib/navigation/history-guard";

// Before React hydrates, so ahead of the App Router's own popstate listener: an unsaved record
// draft can hold the browser's Back and Forward (lib/navigation/history-guard.ts).
if (typeof window !== "undefined") installHistoryGuard();

// Only initialize in browser with valid config
if (
  typeof window !== "undefined" &&
  !isEmbeddedIntegrationPath(window.location.pathname) &&
  process.env.NEXT_PUBLIC_ENABLE_ANALYTICS === "true" &&
  process.env.NEXT_PUBLIC_POSTHOG_KEY &&
  process.env.NEXT_PUBLIC_POSTHOG_HOST
) {
  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
    api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
    // Use localStorage for persistence (first-party, no cookie consent needed)
    persistence: "localStorage",
    // Disable autocapture - we track events explicitly
    autocapture: false,
    // Disable automatic pageview - handled by AnalyticsProvider
    capture_pageview: false,
    // Capture page leave events
    capture_pageleave: true,
    // Enable session recording
    disable_session_recording: false,
    // Create person profiles for all users (including anonymous)
    // Enables session replay analysis of anonymous drop-offs
    person_profiles: "always",
    // Enable automatic exception capture for error tracking
    capture_exceptions: true,
    // Callback when PostHog is fully loaded
    loaded: (ph) => {
      // Register common properties for all events
      ph.register({
        app: "oekopunktemarkt",
        environment: process.env.NODE_ENV === "production" ? "production" : "development",
      });

      // Register attribution data for funnel analysis breakdowns
      const params = new URLSearchParams(window.location.search);
      const utmSource = params.get("utm_source");
      const utmMedium = params.get("utm_medium");
      const utmCampaign = params.get("utm_campaign");
      const utmTerm = params.get("utm_term");
      const utmContent = params.get("utm_content");
      // Google click ids — gclid (standard), gbraid/wbraid (iOS / consent-mode
      // variants). Coalesced into one `gclid` super property for attribution;
      // a paid click can arrive with a gclid but no utm_* (final-URL suffix
      // only), so this also drives the "campaign" traffic_source below. gclid
      // is the join key a future offline-conversion import will rely on
      // (see ARCH-030).
      const gclid = params.get("gclid") ?? params.get("gbraid") ?? params.get("wbraid");

      let referringDomain: string | null = null;
      try {
        referringDomain = document.referrer ? new URL(document.referrer).hostname : null;
      } catch {}

      const searchEngines = [
        "google.com",
        "google.de",
        "bing.com",
        "ecosia.org",
        "duckduckgo.com",
        "yahoo.com",
        "t-online.de",
      ];
      const isOrganic =
        referringDomain && searchEngines.some((se) => referringDomain!.includes(se));

      let trafficSource = "direct";
      if (utmSource || gclid) trafficSource = "campaign";
      else if (isOrganic) trafficSource = "organic_search";
      else if (referringDomain && !referringDomain.includes("oekopunktemarkt"))
        trafficSource = "referral";
      else if (referringDomain?.includes("oekopunktemarkt")) trafficSource = "internal";

      // Session-level properties (overwritten on each page load). Campaign
      // params ABSENT this load are unregistered below, so a later direct /
      // organic visit doesn't carry a stale utm/gclid from an earlier paid
      // landing — which would otherwise tag the event traffic_source=direct
      // alongside a stale gclid (the offline-conversion-import join key, see
      // ARCH-030). First-touch (initial_*) is set-once and intentionally kept.
      const sessionCampaign: Record<string, string> = {
        ...(utmSource && { utm_source: utmSource }),
        ...(utmMedium && { utm_medium: utmMedium }),
        ...(utmCampaign && { utm_campaign: utmCampaign }),
        ...(utmTerm && { utm_term: utmTerm }),
        ...(utmContent && { utm_content: utmContent }),
        ...(gclid && { gclid }),
      };
      ph.register({
        traffic_source: trafficSource,
        referring_domain: referringDomain || "direct",
        landing_page: window.location.pathname,
        ...sessionCampaign,
      });
      // Honor the "overwritten on each page load" intent: drop any campaign
      // param not present this load (register() merges, it never clears).
      for (const key of [
        "utm_source",
        "utm_medium",
        "utm_campaign",
        "utm_term",
        "utm_content",
        "gclid",
      ]) {
        if (!(key in sessionCampaign)) ph.unregister(key);
      }

      // First-touch attribution — set once, never overwritten. This is the
      // acquisition signal for CAC ("which ad acquired this user") and must
      // survive across sessions, because conversions often complete days after
      // the UTM/gclid-tagged landing.
      if (utmSource || gclid) {
        const firstTouch = {
          initial_traffic_source: trafficSource,
          initial_landing_page: window.location.pathname,
          ...(utmSource && { initial_utm_source: utmSource }),
          ...(utmMedium && { initial_utm_medium: utmMedium }),
          ...(utmCampaign && { initial_utm_campaign: utmCampaign }),
          ...(utmTerm && { initial_utm_term: utmTerm }),
          ...(utmContent && { initial_utm_content: utmContent }),
          ...(gclid && { initial_gclid: gclid }),
        };
        // Super properties: ride on client events + are readable via
        // get_property() so getPostHogContext() can forward them to
        // server-side conversion captures.
        ph.register_once(firstTouch);
        // PERSON properties ($set_once = 2nd arg): make the non-dollar
        // initial_* queryable at the PERSON level (recap / CAC dashboards).
        // Previously these were only super properties, so
        // person.properties.initial_* were 100% null.
        ph.setPersonProperties(undefined, firstTouch);
      }
    },
  });
}
